import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { MessageSquareOff } from "lucide-react";
import type { ChatFeedbackItem } from "@/types/chat-feedback";
import { REASON_LABEL } from "@/types/chat-feedback";
import type { PaginationMeta } from "@/types/common";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import { ChatFeedbackPagination } from "./chat-feedback-pagination";
import { RatingBadge } from "./rating-badge";

interface Props {
  data: ChatFeedbackItem[];
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
  onOpenSession: (item: ChatFeedbackItem) => void;
}

const MAX_REASONS_VISIBLE = 3;

function formatRelativeDate(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "방금";
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}일 전`;
  return date.toLocaleDateString("ko-KR");
}

export function ChatFeedbackTable({
  data,
  meta,
  pagination,
  onOpenSession,
}: Props) {
  if (data.length === 0) {
    return (
      <div className="rounded-lg border p-12 text-center space-y-3">
        <MessageSquareOff className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="text-muted-foreground">
          조건에 맞는 피드백이 없습니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">평가</TableHead>
              <TableHead className="w-[240px]">사유</TableHead>
              <TableHead className="w-[200px]">유저</TableHead>
              <TableHead>메시지 미리보기</TableHead>
              <TableHead className="w-[120px]">시점</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => {
              const visibleReasons = item.reasons.slice(0, MAX_REASONS_VISIBLE);
              const overflowCount = item.reasons.length - visibleReasons.length;
              return (
                <TableRow
                  key={item.messageId}
                  className="cursor-pointer hover:bg-muted/60"
                  onClick={() => onOpenSession(item)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenSession(item);
                    }
                  }}
                  aria-label={`피드백 세부 보기 — ${item.rating === "up" ? "추천" : "비추"}`}
                >
                  <TableCell>
                    <RatingBadge rating={item.rating} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {visibleReasons.length === 0 ? (
                        <span className="text-xs text-muted-foreground">
                          (미선택)
                        </span>
                      ) : (
                        visibleReasons.map((r) => (
                          <Badge
                            key={r}
                            variant="secondary"
                            className="truncate max-w-[120px]"
                            title={REASON_LABEL[r as keyof typeof REASON_LABEL] ?? r}
                          >
                            {REASON_LABEL[r as keyof typeof REASON_LABEL] ?? r}
                          </Badge>
                        ))
                      )}
                      {overflowCount > 0 && (
                        <Badge
                          variant="outline"
                          title={item.reasons.join(", ")}
                        >
                          +{overflowCount}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.userEmail || item.userName ? (
                      <div className="flex flex-col">
                        <span className="font-medium text-sm truncate max-w-[180px]">
                          {item.userEmail ?? "(이메일 없음)"}
                        </span>
                        {item.userName && (
                          <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {item.userName}
                          </span>
                        )}
                      </div>
                    ) : (
                      <Badge
                        variant="outline"
                        title="이전 버전 피드백 — userId 미저장"
                      >
                        Anonymous
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm line-clamp-2 max-w-[520px]">
                      {item.messageContentPreview}
                    </p>
                    {item.details && (
                      <p className="text-xs text-muted-foreground mt-1 italic line-clamp-1 max-w-[520px]">
                        “{item.details}”
                      </p>
                    )}
                  </TableCell>
                  <TableCell
                    className="text-xs text-muted-foreground whitespace-nowrap"
                    title={new Date(item.feedbackUpdatedAt).toLocaleString("ko-KR")}
                  >
                    {formatRelativeDate(item.feedbackUpdatedAt)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <ChatFeedbackPagination meta={meta} pagination={pagination} />
    </div>
  );
}
